import {Pressable} from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import React, {FC} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import {tr} from '../../../translations';
import GenericInput from '../../textInput';
import {HeaderSearchInputProps} from '../../../interfaces/components/headerSearchInput';
import TextGeneric from '../../textGeneric';
import Icon from '../../icon';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  min-height: ${({theme}) => theme.text.s1}px;
  flex: 0.9;
  z-index: 3;
`;
const InputContainer = styled(Animated.View)`
  justify-content: center;
  background-color: ${({theme}) => theme.homeBackground};
  border-radius: ${({theme}) => theme.text.s55 / 2}px;
  overflow: hidden;
  flex: 1;
  height: ${({theme}) => theme.text.s1}px;
`;
const SearchInput = styled(GenericInput)`
  width: 100%;
  z-index: 10;
`;
const BadgeContainer = styled(Animated.View)<{charNum: number}>`
  position: absolute;
  align-items: center;
  justify-content: center;
  left: 70%;
  bottom: -${({theme}) => theme.text.s7 / 2}px;
  width: ${({theme, charNum}) =>
    charNum > 2 ? theme.text.s12 * charNum : theme.text.s7}px;
  height: ${({theme}) => theme.text.s7}px;
  border-radius: ${({theme}) => theme.text.s7 / 2}px;
  background-color: ${({theme}) => theme.text.light};
`;
const Badge = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s11}px;
  color: ${({theme}) => theme.header.primary};
  text-align: center;
`;

const HeaderSearchInput: FC<HeaderSearchInputProps> = ({
  scrollY,
  externalOpacity,
  onSearchPress,
  newsCount,
  isLoading,
  ...props
}) => {
  const theme = useTheme() as Theme;
  const {wdp, getByScreenSize} = useResponsiveDimensions();

  const containerAnimatedStyle = useAnimatedStyle(() => {
    const opacity = externalOpacity.value
      ? externalOpacity.value
      : interpolate(scrollY.value, [0, 50], [1, 0]);
    return {
      opacity: opacity,
    };
  });

  const animatedIconStyle = useAnimatedStyle(() => {
    const translateX = interpolate(
      scrollY.value,
      [0, 50],
      [2, -10],
      Extrapolation.CLAMP,
    );
    const color = interpolateColor(
      scrollY.value,
      [0, 50],
      [
        externalOpacity.value ? theme.header.primary : theme.text.grey + 'BB',
        externalOpacity.value ? theme.text.grey + 'BB' : theme.header.primary,
      ],
    );

    return {
      transform: [
        {
          translateX: externalOpacity.value
            ? withTiming(2)
            : withTiming(translateX, {duration: 100}),
        },
      ],
      color,
    };
  });

  const animatedBadgeStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [0, 50],
      [externalOpacity.value ? 1 : 0, externalOpacity.value ? 0 : 1],
      Extrapolation.CLAMP,
    );
    return {
      transform: [
        {
          scale: opacity,
        },
      ],
      opacity,
    };
  });

  return (
    <Container style={{maxWidth: wdp(getByScreenSize(90, 40))}}>
      <Pressable
        onPress={onSearchPress}
        style={{zIndex: 2, position: 'absolute', left: theme.text.s55 / 2}}>
        <Animated.View style={animatedIconStyle}>
          <Icon name={'search'} size={theme.text.s55} />
        </Animated.View>
        {!isLoading && !!newsCount && (
          <BadgeContainer
            charNum={(newsCount + '' || '0')?.length}
            style={animatedBadgeStyle}>
            <Badge>{newsCount}</Badge>
          </BadgeContainer>
        )}
      </Pressable>
      <InputContainer style={containerAnimatedStyle}>
        <SearchInput
          returnKeyType={'search'}
          style={{padding: theme.text.s10}}
          inputContainerStyle={{
            borderWidth: 0,
            backgroundColor: 'transparent',
            marginLeft: theme.text.s55 * 1.7,
            height: '100%',
          }}
          placeholder={tr('search.placeholder')}
          {...props}
        />
      </InputContainer>
    </Container>
  );
};

export default HeaderSearchInput;
